/* ══════════════════════════════════════════════════════════════
   Feature Importance — Top 10 Features Driving Today's Prediction
   Horizontal bar chart with feature names and values
   ══════════════════════════════════════════════════════════════ */

import { fetchMLFeatures } from '../services/ml-api';
import { renderUnavailableBlock } from './data-state';

export interface FeatureImportanceItem {
  name: string;
  value: number;
  importance: number;  // 0-100
  direction: 'bullish' | 'bearish' | 'neutral';
}

// Feature name mapping from 120-dim vector indices to human-readable names
const FEATURE_NAMES = [
  'FII Net 5-Day', 'EMA9 > EMA21', 'ADX Strength', 'RSI (14)',
  'VIX Level', 'MACD Signal', 'PCR Current', 'Bollinger %B',
  'Volume vs 20d', 'Global Risk', 'AD Ratio', 'Delivery %',
  'SMA 50/200', 'ATR (14)', 'Stochastic K', 'OBV Trend',
  'Crude Impact', 'USD/INR', 'Sector Rotation', 'Momentum Score',
];

function mapFeaturesToItems(features: number[]): FeatureImportanceItem[] {
  if (features.length === 0) return [];

  // Take first 20 features (or less), compute importance from absolute value
  const maxAbs = Math.max(...features.slice(0, 20).map(Math.abs), 1);

  const items: FeatureImportanceItem[] = features.slice(0, 20).map((val, i) => ({
    name: FEATURE_NAMES[i] || `Feature ${i + 1}`,
    value: parseFloat(val.toFixed(2)),
    importance: Math.min(Math.round(Math.abs(val) / maxAbs * 100), 100),
    direction: (val > 0.3 ? 'bullish' : val < -0.3 ? 'bearish' : 'neutral') as 'bullish' | 'bearish' | 'neutral',
  }));

  // Sort by importance descending, take top 10
  items.sort((a, b) => b.importance - a.importance);
  return items.slice(0, 10);
}

function renderWithData(container: HTMLElement, data: FeatureImportanceItem[]): void {
  const DIR_COLORS = { bullish: 'var(--india-green)', bearish: 'var(--india-red)', neutral: 'var(--india-saffron)' };
  const DIR_EMOJI = { bullish: '🟢', bearish: '🔴', neutral: '🟡' };

  const bars = data.map((f, i) => `
    <div class="feature-row" style="animation-delay: ${i * 0.04}s">
      <span class="feature-rank">#${i + 1}</span>
      <span class="feature-name">${f.name}</span>
      <div class="feature-bar-track">
        <div class="feature-bar-fill" style="width:${f.importance}%;background:${DIR_COLORS[f.direction]}"></div>
      </div>
      <span class="feature-value">${f.value}</span>
      <span class="feature-dir">${DIR_EMOJI[f.direction]}</span>
    </div>`).join('');

  container.innerHTML = `
    <div class="glass-card feature-importance-panel">
      <div class="card-header">
        <h3>🧠 Feature Importance</h3>
        <span class="feature-subtitle">Top 10 drivers today</span>
      </div>
      <div class="feature-list">${bars}</div>
      <div class="feature-footer">
        <small>120 total features computed • Updated daily pre-market</small>
      </div>
    </div>`;
}

export function renderFeatureImportance(container: HTMLElement, features?: FeatureImportanceItem[]): void {
  if (features) {
    renderWithData(container, features);
  } else {
    container.innerHTML = `<div class="glass-card feature-importance-panel" style="padding:2rem;text-align:center;">Loading feature importance...</div>`;
    fetchMLFeatures().then(featureVec => {
      if (featureVec && featureVec.length > 0) {
        renderWithData(container, mapFeaturesToItems(featureVec));
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card feature-importance-panel', 'Live feature importance unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card feature-importance-panel', 'Live feature importance unavailable');
    });
  }
}
